import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { 
  FileText, Plus, Search, Save, Trash2, Eye, Store, Building2,
  RefreshCw, Printer, CheckCircle, Loader2, Package, Calculator,
  Send, AlertTriangle
} from 'lucide-react';
import { formatDate } from '@/utils/dateFormat';
import api, { productsAPI } from '@/services/api';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700' },
  confirmed: { label: 'Confirmed', color: 'bg-emerald-100 text-emerald-700' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700' }
};

const FranchiseBillingPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const isFranchise = user?.store_type === 'franchise';
  
  // Tab state
  const [activeTab, setActiveTab] = useState('create');
  
  // Create invoice state
  const [franchiseStores, setFranchiseStores] = useState([]);
  const [selectedFranchise, setSelectedFranchise] = useState(null);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  
  // Product search
  const [productSearch, setProductSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Invoice list
  const [invoices, setInvoices] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  
  // View/Print dialog
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewInvoice, setViewInvoice] = useState(null);

  // Redirect if franchise store
  useEffect(() => {
    if (isFranchise) {
      toast({ 
        title: 'Access Denied', 
        description: 'Only owned stores can bill franchise stores',
        variant: 'destructive'
      });
      navigate('/store');
    }
  }, [isFranchise, navigate, toast]);

  // Fetch franchise stores
  const fetchFranchiseStores = useCallback(async () => {
    try {
      const res = await api.get('/store/franchise-invoices/franchise-stores');
      setFranchiseStores(res.data || []);
    } catch (error) {
      console.error('Error fetching franchises:', error);
    }
  }, []);

  // Fetch invoices
  const fetchInvoices = useCallback(async () => {
    setListLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      
      const res = await api.get('/store/franchise-invoices', { params });
      setInvoices(res.data?.items || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setListLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchFranchiseStores();
  }, [fetchFranchiseStores]);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchInvoices();
    }
  }, [activeTab, fetchInvoices]);

  // Handle franchise selection
  const handleFranchiseSelect = (franchiseId) => {
    const franchise = franchiseStores.find(f => f.id === franchiseId);
    setSelectedFranchise(franchise);
    setInvoiceItems([]);
  };

  // Product search
  const handleProductSearch = async (search) => {
    setProductSearch(search);
    if (search.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearchLoading(true);
    try {
      const res = await productsAPI.list({ search, is_active: true, limit: 10 });
      setSearchResults(res.data?.items || res.data || []);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  // Add product to invoice
  const handleAddProduct = async (product) => {
    const exists = invoiceItems.find(i => i.product_id === product.id);
    if (exists) {
      toast({ title: 'Info', description: 'Product already added' });
      return;
    }
    
    // Get franchise price
    let franchisePrice = product.current_price?.cost_price || 0;
    let pricingLevel = 'default';
    
    if (selectedFranchise) {
      try {
        const priceRes = await api.get(
          `/store/franchise-invoices/product-price/${product.id}?franchise_store_id=${selectedFranchise.id}`
        );
        franchisePrice = priceRes.data?.price || franchisePrice;
        pricingLevel = priceRes.data?.pricing_level || 'default';
      } catch (error) {
        console.error('Error fetching franchise price:', error);
      }
    }
    
    setInvoiceItems(prev => [...prev, {
      product_id: product.id,
      product_name: product.name,
      product_sku: product.sku,
      quantity: 1,
      unit_price: franchisePrice,
      pricing_level: pricingLevel,
      discount_percent: 0,
      tax_rate: product.tax_rate || 18
    }]);
    
    setProductSearch('');
    setSearchResults([]);
  };

  // Update item
  const handleUpdateItem = (index, field, value) => {
    setInvoiceItems(prev => prev.map((item, i) => {
      if (i !== index) return item;
      const isNumeric = ['quantity', 'unit_price', 'discount_percent', 'tax_rate'].includes(field);
      return { ...item, [field]: isNumeric ? (parseFloat(value) || 0) : value };
    }));
  };

  // Remove item
  const handleRemoveItem = (index) => {
    setInvoiceItems(prev => prev.filter((_, i) => i !== index));
  };

  // Calculate item total
  const calculateItemTotal = (item) => {
    const gross = item.quantity * item.unit_price;
    const discount = gross * (item.discount_percent / 100);
    const net = gross - discount;
    const tax = net * (item.tax_rate / 100);
    return net + tax;
  };

  // Calculate totals
  const calculateTotals = () => {
    let subtotal = 0;
    let taxTotal = 0;
    let totalQty = 0;
    
    invoiceItems.forEach(item => {
      const gross = item.quantity * item.unit_price;
      const discount = gross * (item.discount_percent / 100);
      const net = gross - discount;
      const tax = net * (item.tax_rate / 100);
      subtotal += net;
      taxTotal += tax;
      totalQty += item.quantity;
    });
    
    return {
      subtotal: subtotal.toFixed(2),
      taxTotal: taxTotal.toFixed(2),
      grandTotal: (subtotal + taxTotal).toFixed(2),
      totalQty,
      totalItems: invoiceItems.length
    };
  };

  const totals = calculateTotals();

  // Submit invoice
  const handleSubmit = async (asDraft = true) => {
    if (!selectedFranchise) {
      toast({ title: 'Error', description: 'Please select a franchise store', variant: 'destructive' });
      return;
    }
    if (invoiceItems.length === 0) {
      toast({ title: 'Error', description: 'Please add at least one product', variant: 'destructive' });
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        franchise_store_id: selectedFranchise.id,
        reference_number: referenceNumber,
        notes: notes,
        items: invoiceItems.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount_percent: item.discount_percent,
          tax_rate: item.tax_rate
        }))
      };
      
      const res = await api.post('/store/franchise-invoices', payload);
      
      // If not draft, confirm immediately
      if (!asDraft) {
        await api.post(`/store/franchise-invoices/${res.data.id}/confirm`);
      }
      
      toast({ 
        title: 'Success', 
        description: `Invoice ${res.data.invoice_number} created${!asDraft ? ' and confirmed' : ''}`,
        variant: 'default'
      });
      
      // Reset form
      setSelectedFranchise(null);
      setInvoiceItems([]);
      setReferenceNumber('');
      setNotes('');
      setActiveTab('history');
      fetchInvoices();
      
    } catch (error) {
      console.error('Submit error:', error);
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to create invoice',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Confirm draft invoice
  const handleConfirmInvoice = async (invoiceId) => {
    try {
      await api.post(`/store/franchise-invoices/${invoiceId}/confirm`);
      toast({ title: 'Success', description: 'Invoice confirmed successfully' });
      fetchInvoices();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to confirm',
        variant: 'destructive'
      });
    }
  };

  // Print invoice
  const printInvoice = (invoice) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Invoice - ${invoice.invoice_number}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
            .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 15px; }
            .header h1 { margin: 0; font-size: 24px; }
            .header h2 { margin: 5px 0; font-size: 14px; color: #666; }
            .parties { display: flex; justify-content: space-between; margin: 20px 0; }
            .party-box { width: 45%; padding: 15px; border: 1px solid #ddd; border-radius: 8px; }
            .party-box h4 { margin: 0 0 10px; font-size: 12px; color: #888; text-transform: uppercase; }
            .party-box p { margin: 3px 0; font-size: 12px; }
            .party-box .name { font-size: 14px; font-weight: 600; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
            th { background-color: #f5f5f5; font-weight: 600; }
            .text-right { text-align: right; }
            .totals { width: 250px; margin-left: auto; margin-top: 20px; }
            .totals tr td { border: none; padding: 5px 10px; }
            .totals .grand-total { font-size: 16px; font-weight: bold; border-top: 2px solid #333 !important; }
            .footer { margin-top: 40px; display: flex; justify-content: space-between; }
            .signature { text-align: center; width: 200px; }
            .signature-line { border-top: 1px solid #333; margin-top: 50px; padding-top: 5px; font-size: 12px; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>TAX INVOICE</h1>
            <h2>${invoice.invoice_number}</h2>
            <p style="margin: 5px 0; font-size: 12px;">Date: ${new Date(invoice.invoice_date || invoice.created_at).toLocaleDateString('en-IN')}</p>
          </div>
          
          <div class="parties">
            <div class="party-box">
              <h4>From (Seller)</h4>
              <p class="name">${invoice.seller_store_name}</p>
              <p>${invoice.seller_store_code}</p>
              ${invoice.seller_gstin ? `<p>GSTIN: ${invoice.seller_gstin}</p>` : ''}
              ${invoice.seller_address ? `<p>${invoice.seller_address.address_line1 || ''}, ${invoice.seller_address.district || invoice.seller_address.city || ''}</p>` : ''}
            </div>
            <div class="party-box">
              <h4>To (Buyer)</h4>
              <p class="name">${invoice.buyer_store_name}</p>
              <p>${invoice.buyer_store_code}</p>
              ${invoice.buyer_gstin ? `<p>GSTIN: ${invoice.buyer_gstin}</p>` : ''}
              ${invoice.buyer_address ? `<p>${invoice.buyer_address.address_line1 || ''}, ${invoice.buyer_address.district || invoice.buyer_address.city || ''}</p>` : ''}
              <p>Pricing Level: ${invoice.buyer_pricing_level || 'Default'}</p>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Product</th>
                <th>HSN</th>
                <th class="text-right">Qty</th>
                <th class="text-right">Rate</th>
                <th class="text-right">Disc%</th>
                <th class="text-right">Tax%</th>
                <th class="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${invoice.items?.map((item, idx) => `
                <tr>
                  <td>${idx + 1}</td>
                  <td>${item.product_name}<br/><small style="color:#666">${item.product_sku}</small></td>
                  <td>${item.hsn_code || '-'}</td>
                  <td class="text-right">${item.quantity}</td>
                  <td class="text-right">₹${item.unit_price?.toFixed(2)}</td>
                  <td class="text-right">${item.discount_percent || 0}%</td>
                  <td class="text-right">${item.tax_rate}%</td>
                  <td class="text-right">₹${item.line_total?.toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <table class="totals">
            <tr>
              <td>Subtotal:</td>
              <td class="text-right">₹${invoice.subtotal?.toFixed(2)}</td>
            </tr>
            <tr>
              <td>Tax:</td>
              <td class="text-right">₹${invoice.tax_total?.toFixed(2)}</td>
            </tr>
            <tr class="grand-total">
              <td>Grand Total:</td>
              <td class="text-right">₹${invoice.grand_total?.toFixed(2)}</td>
            </tr>
          </table>
          
          <div class="footer">
            <div class="signature">
              <div class="signature-line">Prepared By</div>
            </div>
            <div class="signature">
              <div class="signature-line">Authorized Signatory</div>
            </div>
          </div>
          
          <script>window.print();</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (isFranchise) {
    return null;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <FileText className="w-6 h-6 text-purple-600" /> Billing to Franchise
          </h1>
          <p className="text-gray-500">Create invoices for franchise stores</p>
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100">
          <TabsTrigger value="create" className="data-[state=active]:bg-white">
            <Plus className="w-4 h-4 mr-2" /> Create Invoice
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-white">
            <FileText className="w-4 h-4 mr-2" /> Invoice History
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="create" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Franchise Selection */}
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-gray-800 flex items-center gap-2">
                    <Store className="w-5 h-5 text-purple-600" /> Select Franchise Store
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Select value={selectedFranchise?.id || ''} onValueChange={handleFranchiseSelect}>
                    <SelectTrigger className="bg-gray-50 border-gray-300">
                      <SelectValue placeholder="Choose franchise store..." />
                    </SelectTrigger>
                    <SelectContent>
                      {franchiseStores.map(f => (
                        <SelectItem key={f.id} value={f.id}>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{f.name}</span>
                            <span className="text-gray-500">({f.code})</span>
                            <Badge className="bg-purple-100 text-purple-700 text-xs">
                              {f.pricing_level}
                            </Badge>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {selectedFranchise && (
                    <div className="mt-4 p-3 rounded-lg bg-purple-50 border border-purple-200">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-purple-800">{selectedFranchise.name}</p>
                          <p className="text-sm text-purple-600">{selectedFranchise.code}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-purple-600">Pricing Level</p>
                          <Badge className="bg-purple-200 text-purple-800">
                            {selectedFranchise.pricing_level}
                          </Badge>
                        </div>
                      </div>
                      <div className="mt-2 pt-2 border-t border-purple-200">
                        <p className="text-sm text-purple-700">
                          Current Balance: <span className="font-medium">₹{selectedFranchise.current_balance?.toLocaleString() || 0}</span>
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Reference & Notes */}
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardContent className="pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-700">Reference Number</Label>
                      <Input
                        value={referenceNumber}
                        onChange={(e) => setReferenceNumber(e.target.value)}
                        placeholder="Optional reference"
                        className="bg-gray-50 border-gray-300"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Notes</Label>
                      <Input
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Invoice notes..."
                        className="bg-gray-50 border-gray-300"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Product Selection */}
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-gray-800 flex items-center gap-2">
                    <Package className="w-5 h-5 text-teal-600" /> Products
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search products..."
                      value={productSearch}
                      onChange={(e) => handleProductSearch(e.target.value)}
                      className="pl-10 bg-gray-50 border-gray-300"
                      disabled={!selectedFranchise}
                    />
                    {searchResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {searchResults.map(product => (
                          <div
                            key={product.id}
                            onClick={() => handleAddProduct(product)}
                            className="px-4 py-3 hover:bg-teal-50 cursor-pointer border-b border-gray-100"
                          >
                            <p className="font-medium text-gray-800">{product.name}</p>
                            <p className="text-xs text-gray-500">{product.sku}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {!selectedFranchise && (
                    <p className="text-sm text-amber-600 text-center">
                      <AlertTriangle className="w-4 h-4 inline mr-1" />
                      Select a franchise store first to add products
                    </p>
                  )}
                  
                  {/* Items Table */}
                  {invoiceItems.length > 0 ? (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-purple-600 text-white">
                            <TableHead className="text-white font-semibold">#</TableHead>
                            <TableHead className="text-white font-semibold">Product</TableHead>
                            <TableHead className="text-white font-semibold">Qty</TableHead>
                            <TableHead className="text-white font-semibold">Rate</TableHead>
                            <TableHead className="text-white font-semibold">Disc%</TableHead>
                            <TableHead className="text-white font-semibold">Tax%</TableHead>
                            <TableHead className="text-white font-semibold text-right">Amount</TableHead>
                            <TableHead className="w-10"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {invoiceItems.map((item, index) => (
                            <TableRow key={index} className="border-gray-200">
                              <TableCell className="text-gray-500">{index + 1}</TableCell>
                              <TableCell>
                                <p className="font-medium text-gray-800 text-sm">{item.product_name}</p>
                                <p className="text-xs text-gray-500">{item.product_sku}</p>
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  min="1"
                                  value={item.quantity}
                                  onChange={(e) => handleUpdateItem(index, 'quantity', e.target.value)}
                                  className="bg-purple-50 border-purple-200 h-8 w-16"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={item.unit_price}
                                  onChange={(e) => handleUpdateItem(index, 'unit_price', e.target.value)}
                                  className="bg-gray-50 border-gray-300 h-8 w-20"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  min="0"
                                  max="100"
                                  value={item.discount_percent}
                                  onChange={(e) => handleUpdateItem(index, 'discount_percent', e.target.value)}
                                  className="bg-gray-50 border-gray-300 h-8 w-14"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  min="0"
                                  max="100"
                                  value={item.tax_rate}
                                  onChange={(e) => handleUpdateItem(index, 'tax_rate', e.target.value)}
                                  className="bg-gray-50 border-gray-300 h-8 w-14"
                                />
                              </TableCell>
                              <TableCell className="text-right font-medium text-gray-800">
                                ₹{calculateItemTotal(item).toFixed(2)}
                              </TableCell>
                              <TableCell>
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleRemoveItem(index)}>
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : selectedFranchise && (
                    <div className="text-center py-8 border border-dashed border-gray-300 rounded-lg">
                      <Package className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-500">Search and add products above</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
            
            {/* Summary Panel */}
            <div className="space-y-6">
              <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200 shadow-sm sticky top-6">
                <CardHeader className="pb-3">
                  <CardTitle className="text-purple-800 flex items-center gap-2">
                    <Calculator className="w-5 h-5" /> Invoice Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Items:</span>
                      <span className="font-medium">{totals.totalItems}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Qty:</span>
                      <span className="font-medium">{totals.totalQty}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-medium">₹{totals.subtotal}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Tax:</span>
                      <span className="font-medium">₹{totals.taxTotal}</span>
                    </div>
                    <hr className="border-purple-300" />
                    <div className="flex justify-between text-lg font-bold">
                      <span className="text-purple-800">Total:</span>
                      <span className="text-teal-600">₹{totals.grandTotal}</span>
                    </div>
                  </div>
                  
                  <div className="space-y-2 pt-4">
                    <Button 
                      className="w-full bg-purple-600 hover:bg-purple-700"
                      onClick={() => handleSubmit(false)}
                      disabled={submitting || invoiceItems.length === 0 || !selectedFranchise}
                    >
                      {submitting ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Create & Confirm
                    </Button>
                    <Button 
                      variant="outline"
                      className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
                      onClick={() => handleSubmit(true)}
                      disabled={submitting || invoiceItems.length === 0 || !selectedFranchise}
                    >
                      Save as Draft
                    </Button>
                  </div>
                  
                  <div className="p-3 rounded-lg bg-white/50 border border-purple-200 mt-4">
                    <p className="text-xs text-purple-700">
                      <AlertTriangle className="w-3 h-3 inline mr-1" />
                      Confirmed invoices will update franchise ledger and deduct stock
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
        
        {/* History Tab */}
        <TabsContent value="history" className="mt-6">
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-gray-800">Invoice History</CardTitle>
                <div className="flex gap-2">
                  <Select value={statusFilter || 'all'} onValueChange={(val) => setStatusFilter(val === 'all' ? '' : val)}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={fetchInvoices}>
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {listLoading ? (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-purple-600" />
                </div>
              ) : invoices.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">No invoices found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Franchise</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="w-24">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map(inv => (
                      <TableRow key={inv.id}>
                        <TableCell className="font-medium">{inv.invoice_number}</TableCell>
                        <TableCell>
                          <p>{inv.buyer_store_name}</p>
                          <p className="text-xs text-gray-500">{inv.buyer_store_code}</p>
                        </TableCell>
                        <TableCell>{inv.items?.length || 0}</TableCell>
                        <TableCell>{formatDate(inv.created_at)}</TableCell>
                        <TableCell>
                          <Badge className={STATUS_CONFIG[inv.status]?.color}>
                            {STATUS_CONFIG[inv.status]?.label || inv.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">₹{inv.grand_total?.toFixed(2)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-7 w-7"
                              onClick={() => { setViewInvoice(inv); setShowViewDialog(true); }}
                            >
                              <Eye className="w-4 h-4 text-gray-500" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-7 w-7"
                              onClick={() => printInvoice(inv)}
                            >
                              <Printer className="w-4 h-4 text-gray-500" />
                            </Button>
                            {inv.status === 'draft' && (
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-7 w-7"
                                onClick={() => handleConfirmInvoice(inv.id)}
                              >
                                <CheckCircle className="w-4 h-4 text-emerald-500" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* View Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" /> Invoice - {viewInvoice?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          {viewInvoice && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="p-3 rounded bg-gray-50">
                  <p className="text-gray-500 text-xs mb-1">FROM</p>
                  <p className="font-medium">{viewInvoice.seller_store_name}</p>
                  <p className="text-gray-600">{viewInvoice.seller_store_code}</p>
                </div>
                <div className="p-3 rounded bg-purple-50">
                  <p className="text-gray-500 text-xs mb-1">TO</p>
                  <p className="font-medium">{viewInvoice.buyer_store_name}</p>
                  <p className="text-purple-600">{viewInvoice.buyer_store_code}</p>
                </div>
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Rate</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {viewInvoice.items?.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <p className="font-medium">{item.product_name}</p>
                        <p className="text-xs text-gray-500">{item.product_sku}</p>
                      </TableCell>
                      <TableCell className="text-right">{item.quantity}</TableCell>
                      <TableCell className="text-right">₹{item.unit_price?.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-medium">₹{item.line_total?.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              <div className="flex justify-end">
                <div className="w-48 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span>₹{viewInvoice.subtotal?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Tax:</span>
                    <span>₹{viewInvoice.tax_total?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg border-t pt-1">
                    <span>Total:</span>
                    <span>₹{viewInvoice.grand_total?.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => printInvoice(viewInvoice)}>
              <Printer className="w-4 h-4 mr-2" /> Print
            </Button>
            <Button onClick={() => setShowViewDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FranchiseBillingPage;
